import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import SoftSelect from 'components/SoftSelect';
import Grid from '@mui/material/Grid';
import SoftDatePicker from 'components/SoftDatePicker';
import SoftTypography from 'components/SoftTypography';
import axios from 'axios';

// Auth
import { AuthContext } from 'context/AuthContext';

function ModalFiltros({ show, handleClose, onApplyFilters, initialFilters }) {
  const defaultFilters = {
    cliente: null,
    fechaDesde: null,
    fechaHasta: null,
    incoterms: null,
    estado: null,
    numeroOrden: '',
  };

  const [filters, setFilters] = useState(defaultFilters);
  const [clienteOptions, setClienteOptions] = useState([]);

  const { hasRole } = useContext(AuthContext);
  const isClient = hasRole('client');

  const estadoOptions = [
    { value: 'En Proceso', label: 'En Proceso' },
    { value: 'Recibida', label: 'Recibida' },
    { value: 'Enviada', label: 'Enviada' },
    { value: 'Entregada', label: 'Entregada' },
    { value: 'Anulada', label: 'Anulada' },
  ];

  const incotermsOptions = [
    { value: 'EXW', label: 'EXW' },
    { value: 'FOB', label: 'FOB' },
    { value: 'CIF', label: 'CIF' },
    { value: 'DDP', label: 'DDP' },
  ];

  // Sincroniza el estado interno con initialFilters cada vez que se abre
  useEffect(() => {
    if (show) {
      setFilters(initialFilters || defaultFilters);
    }
  }, [show, initialFilters]);

  // Carga de clientes (NO para rol client)
  useEffect(() => {
    if (!show || isClient) return;
    const fetchClients = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/maintenance/clients/`
        );
        setClienteOptions(data.map((c) => ({ value: c.id, label: c.nombre })));
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };
    fetchClients();
  }, [show, isClient]);

  const handleApplyFilters = () => {
    // Para clientes, ignoramos el filtro de cliente (por si venía arrastrado)
    const payload = isClient ? { ...filters, cliente: null } : filters;
    onApplyFilters(payload);
    handleClose();
  };

  const handleReset = () => {
    const reset = { ...defaultFilters };
    if (isClient) reset.cliente = null; // asegurar nulo para cliente
    setFilters(reset);
    onApplyFilters(reset);
    handleClose();
  };

  const handleFechaDesdeChange = (date) => {
    const selectedDate = date[0];
    setFilters((prev) => {
      if (selectedDate && !prev.fechaHasta) {
        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);
        return { ...prev, fechaDesde: selectedDate, fechaHasta: nextDay };
      }
      return { ...prev, fechaDesde: selectedDate };
    });
  };

  const handleFechaHastaChange = (date) => {
    const selectedDate = date[0];
    setFilters((prev) => {
      if (selectedDate && !prev.fechaDesde) {
        const previousDay = new Date(selectedDate);
        previousDay.setDate(previousDay.getDate() - 1);
        return { ...prev, fechaHasta: selectedDate, fechaDesde: previousDay };
      }
      return { ...prev, fechaHasta: selectedDate };
    });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdropClassName="modal-backdrop"
      backdrop="static"
      className="modal-container small"
      style={{ zIndex: 1050 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Filtros de Búsqueda</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SoftBox mt={2}>
          <Grid container spacing={3}>
            {/* Número de Orden */}
            <Grid item xs={12} md={6}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  Número de Orden
                </SoftTypography>
                <SoftInput
                  type="text"
                  value={filters.numeroOrden}
                  onChange={(e) => setFilters((prev) => ({ ...prev, numeroOrden: e.target.value }))}
                  placeholder="Ingrese número de orden..."
                />
                <SoftTypography variant="caption" color="dark">
                  <p>Ingrese el número de orden para filtrar.</p>
                </SoftTypography>
              </SoftBox>
            </Grid>

            {/* Cliente (oculto para rol client) */}
            {!isClient && (
              <Grid item xs={12} md={6}>
                <SoftBox display="flex" flexDirection="column" gap={1}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text">
                    Cliente
                  </SoftTypography>
                  <SoftSelect
                    options={clienteOptions}
                    value={filters.cliente}
                    onChange={(selected) => setFilters((prev) => ({ ...prev, cliente: selected }))}
                    placeholder="Seleccionar cliente..."
                    isClearable
                  />
                  <SoftTypography variant="caption" color="dark">
                    <p>Selecciona el cliente para filtrar las órdenes de compra asociadas.</p>
                  </SoftTypography>
                </SoftBox>
              </Grid>
            )}

            {/* Incoterms */}
            <Grid item xs={12} md={6}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  Incoterms
                </SoftTypography>
                <SoftSelect
                  options={incotermsOptions}
                  value={filters.incoterms}
                  onChange={(selected) => setFilters((prev) => ({ ...prev, incoterms: selected }))}
                  placeholder="Seleccionar incoterm..."
                  isClearable
                />
                <SoftTypography variant="caption" color="dark">
                  <p>Selecciona el incoterm para filtrar las órdenes de compra.</p>
                </SoftTypography>
              </SoftBox>
            </Grid>

            {/* Estado */}
            <Grid item xs={12} md={6}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  Estado
                </SoftTypography>
                <SoftSelect
                  options={estadoOptions}
                  value={filters.estado}
                  onChange={(selected) => setFilters((prev) => ({ ...prev, estado: selected }))}
                  placeholder="Seleccionar estado..."
                  isClearable
                />
                <SoftTypography variant="caption" color="dark">
                  <p>Selecciona el estado de la orden de compra para filtrar.</p>
                </SoftTypography>
              </SoftBox>
            </Grid>

            {/* Fecha Desde */}
            <Grid item xs={12} md={6}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  Fecha Desde
                </SoftTypography>
                <SoftDatePicker
                  value={filters.fechaDesde}
                  onChange={handleFechaDesdeChange}
                  options={{
                    dateFormat: 'Y-m-d',
                    altInput: true,
                    altFormat: 'F j, Y',
                  }}
                />
                <SoftTypography variant="caption" color="dark">
                  <p>Selecciona la fecha inicial del rango para filtrar las órdenes de compra.</p>
                </SoftTypography>
              </SoftBox>
            </Grid>

            {/* Fecha Hasta */}
            <Grid item xs={12} md={6}>
              <SoftBox display="flex" flexDirection="column" gap={1}>
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  Fecha Hasta
                </SoftTypography>
                <SoftDatePicker
                  value={filters.fechaHasta}
                  onChange={handleFechaHastaChange}
                  options={{
                    dateFormat: 'Y-m-d',
                    altInput: true,
                    altFormat: 'F j, Y',
                  }}
                />
                <SoftTypography variant="caption" color="dark">
                  <p>Selecciona la fecha final del rango para filtrar las órdenes de compra.</p>
                </SoftTypography>
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>
      </Modal.Body>
      <Modal.Footer>
        <SoftButton variant="gradient" color="warning" onClick={handleReset} sx={{ mr: 2 }}>
          Limpiar
        </SoftButton>
        <SoftButton variant="gradient" color="success" onClick={handleApplyFilters} sx={{ mr: 2 }}>
          Aplicar Filtros
        </SoftButton>
        <SoftButton variant="gradient" color="error" onClick={handleClose}>
          Cancelar
        </SoftButton>
      </Modal.Footer>
    </Modal>
  );
}

ModalFiltros.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  initialFilters: PropTypes.shape({
    cliente: PropTypes.object,
    fechaDesde: PropTypes.any,
    fechaHasta: PropTypes.any,
    incoterms: PropTypes.object,
    estado: PropTypes.object,
    numeroOrden: PropTypes.string,
  }),
};

export default ModalFiltros;
